import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useViewImageContext } from "./ViewImageContext";
import { useViewLandContext } from "../ViewLandContext";
import { useLandOperationSummary } from "../useLandOperationSummary";
import { KTSVG } from "../../../../../../../_metronic/helpers";
import { useIsMobile } from "../../../../../../shared/responsive";
import { scrollElementIntoMobilePanel } from "../../../../../../shared/scrollIntoMobilePanel";
import DateSelector from "./DateSelector";
import PlanetHdDateSelector from "./PlanetHdDateSelector";
import ProductFirstImageSelector from "./ProductFirstImageSelector";
import YieldMapSelector from "./YieldMapSelector";
import {PLANET_HD_V1_ENABLED} from "../../../../../../config/featureFlags";
import {
  HD_IMAGE_TYPES,
  IMAGE_TYPES_WITH_DATE,
  STANDARD_IMAGE_TYPES,
  isHdImageType,
} from "./imageTypeUtils";
import {shouldShowImageLayerRetry} from "./imageLayerLoadState";
import {REMOTE_SENSING_PRODUCTS} from "./remoteSensingModel";
import "./Images.style.scss";

const CreateImageAmbientAction = ({ canCreateImageAmbient, openImageAmbient }) => {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={openImageAmbient}
      disabled={!canCreateImageAmbient}
      className={`view-land-image-action field-view-action-button ${canCreateImageAmbient ? "" : "disabled"}`.trim()}
    >
      <span className="view-land-image-action__icon">
        <KTSVG path="/media/icons/duotune/maps/map002.svg" className="svg-icon svg-icon-2" />
      </span>
      <span className="view-land-image-action__title field-view-action-link__title">
        {t("images:buttons.create_ambientation")}
      </span>
    </button>
  );
};

const IMAGE_TYPE_CHOICES = [
  {
    imageType: STANDARD_IMAGE_TYPES.RGB,
    labelKey: "visible",
    image: "/media/images/rgb.png",
  },
  {
    imageType: HD_IMAGE_TYPES.RGB,
    labelKey: "visible_hd",
    image: "/media/images/rgb.png",
  },
  {
    imageType: STANDARD_IMAGE_TYPES.NDVI,
    labelKey: "ndvi",
    image: "/media/images/ndvi.png",
  },
  {
    imageType: HD_IMAGE_TYPES.NDVI,
    labelKey: "ndvi_hd",
    image: "/media/images/ndvi.png",
  },
  {
    imageType: STANDARD_IMAGE_TYPES.EVI,
    labelKey: "evi",
    image: "/media/images/evi.png",
  },
  {
    imageType: HD_IMAGE_TYPES.EVI,
    labelKey: "evi_hd",
    image: "/media/images/evi.png",
  },
];

const IMAGE_TYPE_CHOICE_GROUPS = [
  IMAGE_TYPE_CHOICES.slice(0, 2),
  IMAGE_TYPE_CHOICES.slice(2, 4),
  IMAGE_TYPE_CHOICES.slice(4, 6),
];

const SoilMapChoice = ({
  active,
  disabled,
  isMobile,
  onRetry,
  onSelect,
  soilMapState,
}) => {
  const { t } = useTranslation();
  const loading = Boolean(soilMapState.loading);
  const available = soilMapState.status === "available";
  const noCoverage = soilMapState.status === "no_coverage";
  const sourceUnavailable = soilMapState.status === "source_unavailable";
  const choiceDisabled = disabled || loading || noCoverage;
  const soilMapLabel = t("images:selector.soil_map");
  let statusLabel = t("images:soil_map.on_demand");

  if (available) {
    statusLabel = t("images:soil_map.available");
  } else if (loading) {
    statusLabel = t("images:soil_map.loading");
  } else if (noCoverage) {
    statusLabel = t("images:soil_map.no_coverage");
  } else if (sourceUnavailable) {
    statusLabel = t("images:soil_map.source_unavailable");
  }
  const handleClick = () => {
    if (available || soilMapState.status === "idle") {
      onSelect();
      return;
    }

    if (sourceUnavailable && !disabled) {
      onRetry();
    }
  };
  const classNames = [
    isMobile
      ? "view-land-image-chip view-land-image-chip--wide view-land-soil-map-choice"
      : "d-flex align-items-center div-type-selector rounded p-5 view-land-image-choice view-land-soil-map-choice",
    active ? "active" : "",
    available ? "available" : "",
    choiceDisabled ? "disabled" : "",
    noCoverage ? "no-coverage" : "",
    sourceUnavailable ? "source-unavailable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      disabled={choiceDisabled}
      onClick={handleClick}
      className={classNames}
      aria-label={available ? soilMapLabel : `${soilMapLabel}. ${statusLabel}`}
    >
      {!isMobile && (
        <div className="symbol symbol-45px symbol-circle me-5" aria-hidden="true">
          <div className="symbol-label" style={{ backgroundImage: "url(/media/images/soil-map.png)" }} />
        </div>
      )}
      <span className={isMobile ? "view-land-soil-map-choice__content" : "flex-grow-1 me-2"}>
        <span className={isMobile ? undefined : "fw-bold text-gray-800 text-hover-primary fs-7"}>
          {soilMapLabel}
        </span>
        {loading && <span className="spinner-border spinner-border-sm align-middle ms-2" aria-hidden="true" />}
      </span>
      {sourceUnavailable && !disabled && (
        <span className="view-land-soil-map-choice__retry">
          {t("images:soil_map.retry")}
        </span>
      )}
    </button>
  );
};

const ImageTypeChoice = ({
  canCreateImageAmbient,
  openImageAmbient,
  requestDateSelection,
  showRemoteSensingChoices = true,
}) => {
  const { t } = useTranslation();
  const viewImageContext = useViewImageContext();
  const viewLandContext = useViewLandContext();
  const isMobile = useIsMobile();
  const land = viewLandContext.land;
  const activeLandImage = viewImageContext.getActiveLandImage(land.land_id);
  const activedChoice = activeLandImage.imageType;
  const selectedDate = activeLandImage.selectedDate;
  const disabled = viewImageContext.loading;
  const soilMapState = viewImageContext.getLandSoilMapState(land.land_id);
  const hdAccess = viewImageContext.getHdAccessStatus(land.land_id);
  const hdDisabled = disabled || hdAccess.loading || !hdAccess.enabled;
  const hdDisabledTitle = hdAccess.loading
    ? t("images:hd_status.loading")
    : t("images:hd_status.disabled");

  const applyImageType = (nextImageType) => {
    const shouldResetDate = isHdImageType(activedChoice) !== isHdImageType(nextImageType);

    viewImageContext.changeImageType(land, nextImageType);

    if (shouldResetDate) {
      viewImageContext.changeDate(land, null);
    }

    if (IMAGE_TYPES_WITH_DATE.has(nextImageType) && (selectedDate == null || shouldResetDate)) {
      requestDateSelection?.();
    }
  };

  const changeImageType = (nextImageType) => {
    if (disabled) {
      return;
    }

    if (isHdImageType(nextImageType)) {
      if (hdDisabled) {
        return;
      }

      applyImageType(nextImageType);
      return;
    }

    applyImageType(nextImageType);
  };

  const getChoiceDisabled = (nextImageType) => disabled || (isHdImageType(nextImageType) && hdDisabled);

  if (isMobile) {
    return (
      <div className="view-land-image-chip-list">
        {showRemoteSensingChoices && IMAGE_TYPE_CHOICE_GROUPS.map((group) => {
          const groupKey = group.map((choice) => choice.imageType).join("-");

          return (
            <div key={groupKey} className="view-land-image-choice-pair view-land-image-choice-pair--chips">
              {group.map((choice) => {
                const choiceDisabled = getChoiceDisabled(choice.imageType);
                const choiceHdUnavailable =
                  isHdImageType(choice.imageType) &&
                  !disabled &&
                  !hdAccess.loading &&
                  !hdAccess.enabled;

                return (
                  <button
                    type="button"
                    key={choice.imageType}
                    disabled={choiceDisabled}
                    title={isHdImageType(choice.imageType) && choiceDisabled && !disabled ? hdDisabledTitle : undefined}
                    onClick={() => changeImageType(choice.imageType)}
                    className={`view-land-image-chip ${activedChoice === choice.imageType ? 'active' : ''} ${choiceDisabled ? 'disabled' : ''} ${choiceHdUnavailable ? 'hd-unavailable' : ''}`.trim()}
                  >
                    <span>{t(`images:selector.${choice.labelKey}`)}</span>
                    {(activedChoice === choice.imageType && viewImageContext.loading) && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
                  </button>
                );
              })}
            </div>
          );
        })}

        {showRemoteSensingChoices && (
          <span className="view-land-image-choice-separator" aria-hidden="true" />
        )}

        <button
          type="button"
          disabled={disabled}
          onClick={() => changeImageType(STANDARD_IMAGE_TYPES.ALTIMETRY)}
          className={`view-land-image-chip view-land-image-chip--wide ${activedChoice === 'altimetry' ? 'active' : ''} ${disabled ? 'disabled' : ''}`.trim()}
        >
          <span>{t("images:selector.altimetry")}</span>
          {(activedChoice === 'altimetry' && viewImageContext.loading) && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
        </button>

        <SoilMapChoice
          active={activedChoice === STANDARD_IMAGE_TYPES.SOIL_MAP}
          disabled={disabled}
          isMobile
          soilMapState={soilMapState}
          onRetry={() => viewImageContext.ensureLandSoilMap(land.land_id, { force: true })}
          onSelect={() => changeImageType(STANDARD_IMAGE_TYPES.SOIL_MAP)}
        />

        <button
          type="button"
          disabled={disabled}
          onClick={() => changeImageType(STANDARD_IMAGE_TYPES.YIELD_MAP)}
          className={`view-land-image-chip view-land-image-chip--wide ${activedChoice === STANDARD_IMAGE_TYPES.YIELD_MAP ? 'active' : ''} ${disabled ? 'disabled' : ''}`.trim()}
        >
          <span>{t("images:selector.yield_map")}</span>
          {(activedChoice === STANDARD_IMAGE_TYPES.YIELD_MAP && viewImageContext.loading) && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
        </button>

      </div>
    );
  }

  return (
    <>
      {showRemoteSensingChoices && IMAGE_TYPE_CHOICE_GROUPS.map((group) => {
        const groupKey = group.map((choice) => choice.imageType).join("-");
        const activeChoiceInGroup = group.find((choice) => activedChoice === choice.imageType);
        const shouldShowInlineAction =
          activeChoiceInGroup != null &&
          (activeChoiceInGroup.imageType === STANDARD_IMAGE_TYPES.EVI || activeChoiceInGroup.imageType === HD_IMAGE_TYPES.EVI) &&
          canCreateImageAmbient;

        return (
          <div key={groupKey} className="view-land-image-choice-group">
            <div className="view-land-image-choice-pair">
              {group.map((choice) => {
                const choiceDisabled = getChoiceDisabled(choice.imageType);
                const choiceHdUnavailable =
                  isHdImageType(choice.imageType) &&
                  !disabled &&
                  !hdAccess.loading &&
                  !hdAccess.enabled;

                return (
                  <div
                    key={choice.imageType}
                    onClick={() => changeImageType(choice.imageType)}
                    title={isHdImageType(choice.imageType) && choiceDisabled && !disabled ? hdDisabledTitle : undefined}
                    role="button"
                    aria-disabled={choiceDisabled}
                    className={`d-flex align-items-center div-type-selector rounded p-5 view-land-image-choice ${activedChoice === choice.imageType ? 'active' : ''} ${choiceDisabled ? 'disabled' : ''} ${choiceHdUnavailable ? 'hd-unavailable' : ''}`.trim()}
                  >
                    <div className="symbol symbol-45px symbol-circle me-5">
                      <div className="symbol-label" style={{ backgroundImage: `url(${choice.image})` }}></div>
                    </div>
                    <div className="flex-grow-1 me-2">
                      <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t(`images:selector.${choice.labelKey}`)}</span>
                      {(activedChoice === choice.imageType && viewImageContext.loading) && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {shouldShowInlineAction && (
              <div className="view-land-image-choice-inline-action">
                <CreateImageAmbientAction
                  canCreateImageAmbient={canCreateImageAmbient}
                  openImageAmbient={openImageAmbient}
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="view-land-image-choice-separator" aria-hidden="true" />

      <div onClick={() => changeImageType(STANDARD_IMAGE_TYPES.ALTIMETRY)} className={`d-flex align-items-center div-type-selector rounded p-5 view-land-image-choice ` + (activedChoice === 'altimetry' ? 'active' : '') + ` ` + (disabled ? 'disabled' : '')}>
        <div className="symbol symbol-45px symbol-circle me-5">
          <div className="symbol-label" style={{ backgroundImage: "url(/media/images/altimetry.png)" }}></div>
        </div>

        <div className="flex-grow-1 me-2">
          <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("images:selector.altimetry")}</span>
          {(activedChoice === 'altimetry' && viewImageContext.loading) && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
        </div>
      </div>

      <SoilMapChoice
        active={activedChoice === STANDARD_IMAGE_TYPES.SOIL_MAP}
        disabled={disabled}
        soilMapState={soilMapState}
        onRetry={() => viewImageContext.ensureLandSoilMap(land.land_id, { force: true })}
        onSelect={() => changeImageType(STANDARD_IMAGE_TYPES.SOIL_MAP)}
      />

      <div onClick={() => changeImageType(STANDARD_IMAGE_TYPES.YIELD_MAP)} className={`d-flex align-items-center div-type-selector rounded p-5 view-land-image-choice ` + (activedChoice === STANDARD_IMAGE_TYPES.YIELD_MAP ? 'active' : '') + ` ` + (disabled ? 'disabled' : '')}>
        <div className="symbol symbol-45px symbol-circle me-5">
          <div className="symbol-label" style={{ backgroundImage: "url(/media/images/yield-map.png)" }}></div>
        </div>

        <div className="flex-grow-1 me-2">
          <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("images:selector.yield_map")}</span>
          {(activedChoice === STANDARD_IMAGE_TYPES.YIELD_MAP && viewImageContext.loading) && <span className='spinner-border spinner-border-sm align-middle ms-2'></span>}
        </div>
      </div>
    </>
  );
};

const Images = () => {
  const { t } = useTranslation();
  const viewImageContext = useViewImageContext();
  const viewLandContext = useViewLandContext();
  const isMobile = useIsMobile();
  const eviExtrasRef = useRef(null);
  const previousImageTypeRef = useRef(null);
  const [datePickerOpenRequest, setDatePickerOpenRequest] = useState({
    product: null,
    signal: 0,
  });
  const [hdPreparingImage, setHdPreparingImage] = useState(false);
  const landOperations = useLandOperationSummary();
  const canCreateImageAmbient = landOperations.image.canCreateImageAmbient;
  const activeImageType = landOperations.image.imageType;
  const landId = viewLandContext.land?.land_id;
  const imageLayerLoadState = viewImageContext.getImageLayerLoadState(landId);
  const activeImageIsHd = isHdImageType(activeImageType);
  const activeProduct = activeImageIsHd
    ? REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
    : REMOTE_SENSING_PRODUCTS.SENTINEL;
  const datePickerOpenSignal = datePickerOpenRequest.product === activeProduct
    ? datePickerOpenRequest.signal
    : 0;
  const requestDateSelection = (product = activeProduct) => {
    setDatePickerOpenRequest((current) => ({
      product,
      signal: current.signal + 1,
    }));
  };
  const consumeDateSelectionRequest = (signal) => {
    setDatePickerOpenRequest((current) =>
      current.product === activeProduct && current.signal === signal
        ? {...current, product: null}
        : current
    );
  };
  const shouldShowDateSelector =
    activeImageType !== STANDARD_IMAGE_TYPES.ALTIMETRY &&
    activeImageType !== STANDARD_IMAGE_TYPES.SOIL_MAP &&
    activeImageType !== STANDARD_IMAGE_TYPES.YIELD_MAP &&
    (!activeImageIsHd || PLANET_HD_V1_ENABLED);
  const shouldShowYieldMapSelector = activeImageType === STANDARD_IMAGE_TYPES.YIELD_MAP;
  const shouldShowPlanetHdCalendar =
    PLANET_HD_V1_ENABLED && activeImageIsHd;
  const dateSelector = shouldShowDateSelector && (
    shouldShowPlanetHdCalendar
      ? <PlanetHdDateSelector
          openSignal={datePickerOpenSignal}
          onOpenSignalHandled={consumeDateSelectionRequest}
          onPreparingChange={setHdPreparingImage}
        />
      : <DateSelector
          openSignal={datePickerOpenSignal}
          onOpenSignalHandled={consumeDateSelectionRequest}
        />
  );
  const openImageAmbient = () => {
    if (canCreateImageAmbient) {
      viewImageContext.setOpenImageAmbient(true);
    }
  };

  useEffect(() => {
    const previousImageType = previousImageTypeRef.current;
    previousImageTypeRef.current = activeImageType;

    if (
      !isMobile ||
      previousImageType == null ||
      previousImageType === activeImageType ||
      (activeImageType !== STANDARD_IMAGE_TYPES.EVI && activeImageType !== HD_IMAGE_TYPES.EVI)
    ) {
      return undefined;
    }

    return scrollElementIntoMobilePanel(eviExtrasRef.current, { delay: 180 });
  }, [activeImageType, isMobile]);

  return (
    <div className={`view-land-image-stack${PLANET_HD_V1_ENABLED ? ' view-land-image-stack--product-first' : ''}`}>
      {PLANET_HD_V1_ENABLED && (
        <>
          <h3 className="view-land-image-stack__title">{t('images:satellite_images')}</h3>
          <ProductFirstImageSelector
            preparingImage={hdPreparingImage}
            canCreateImageAmbient={canCreateImageAmbient}
            openImageAmbient={openImageAmbient}
            requestDateSelection={requestDateSelection}
            dateSelector={dateSelector}
          />
        </>
      )}
      {!PLANET_HD_V1_ENABLED && dateSelector}
      {shouldShowYieldMapSelector && <YieldMapSelector />}
      {shouldShowImageLayerRetry(activeImageType, imageLayerLoadState) && (
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-sm btn-light-danger"
            onClick={() => viewImageContext.retryImageLayer(landId)}
          >
            {t('images:buttons.retry_image', {defaultValue: 'Reintentar carga de imagen'})}
          </button>
        </div>
      )}
      <div className="view-land-image-choice-list">
        <ImageTypeChoice
          canCreateImageAmbient={canCreateImageAmbient}
          openImageAmbient={openImageAmbient}
          requestDateSelection={requestDateSelection}
          showRemoteSensingChoices={!PLANET_HD_V1_ENABLED}
        />
      </div>
      {!PLANET_HD_V1_ENABLED && isMobile && canCreateImageAmbient && (
        <div ref={eviExtrasRef} className="view-land-image-evi-extras">
          <CreateImageAmbientAction
            canCreateImageAmbient={canCreateImageAmbient}
            openImageAmbient={openImageAmbient}
          />
        </div>
      )}
    </div>
  );
};


export default Images;
